import { useEffect } from 'react';
import { APP_PRESS_MIN_MS } from './useButtonPress';

/** Primitives manage their own press timing. */
const MANAGED_SELECTOR = '.app-btn-pill, .app-icon-btn';

function isManagedButton(btn) {
  return btn.matches(MANAGED_SELECTOR);
}

/**
 * Applies minimum-duration `data-pressed` to native <button> elements in the app shell
 * that are not already using Button / IconButton primitives.
 */
export function useGlobalButtonPress(containerRef) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const pressState = new WeakMap();
    const activeButtons = new Set();

    const scheduleRelease = (btn, startTime) => {
      const prev = pressState.get(btn);
      if (prev?.timer) clearTimeout(prev.timer);

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, APP_PRESS_MIN_MS - elapsed);

      const timer = setTimeout(() => {
        btn.removeAttribute('data-pressed');
        pressState.delete(btn);
        activeButtons.delete(btn);
      }, remaining);

      pressState.set(btn, { startTime, timer });
    };

    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      const btn = event.target.closest('button');
      if (!btn || !root.contains(btn) || btn.disabled || isManagedButton(btn)) return;

      const prev = pressState.get(btn);
      if (prev?.timer) clearTimeout(prev.timer);

      const startTime = Date.now();
      btn.setAttribute('data-pressed', 'true');
      pressState.set(btn, { startTime, timer: null });
      activeButtons.add(btn);
    };

    const onPointerEnd = (event) => {
      const btn = event.target.closest('button');
      if (!btn || !root.contains(btn) || isManagedButton(btn)) return;

      const state = pressState.get(btn);
      if (!state) return;
      scheduleRelease(btn, state.startTime);
    };

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('pointerup', onPointerEnd);
    root.addEventListener('pointercancel', onPointerEnd);
    root.addEventListener('pointerleave', onPointerEnd);

    return () => {
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('pointerup', onPointerEnd);
      root.removeEventListener('pointercancel', onPointerEnd);
      root.removeEventListener('pointerleave', onPointerEnd);
      activeButtons.forEach((btn) => {
        const state = pressState.get(btn);
        if (state?.timer) clearTimeout(state.timer);
        btn.removeAttribute('data-pressed');
      });
      activeButtons.clear();
    };
  }, [containerRef]);
}
